<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useField } from 'vee-validate'
import { UploadCloud, Video, Loader2, Trash2, AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { useFileBucketManager } from '../../hooks/useFileBucketManager'
import { extractFileNameFromUrl } from '../../utils/fileName'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'VIDEO' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

interface UploadedVideoInfo {
  url: string
  name: string
  size?: number
  type?: string
  bucketFilename?: string
}

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage: fieldErrorMessage,
  handleChange,
  setValue,
  setTouched
} = useField<string | null>(fieldName, undefined, {
  initialValue: props.defaultValue ?? null
})

const dragActive = ref(false)
const uploadedVideo = ref<UploadedVideoInfo | null>(null)
const localPreviewUrl = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const didInit = ref(false)

const fileBucketManager = computed(() => 
  props.field.fileToBucketManage
    ? useFileBucketManager(props.field.fileToBucketManage)
    : null
)

const hasUploadSupport = computed(() => fileBucketManager.value?.hasUploadSupport ?? false)
const hasDeleteSupport = computed(() => fileBucketManager.value?.hasDeleteSupport ?? false)
const isUploading = computed(() => fileBucketManager.value?.isUploading ?? false)
const isDeleting = computed(() => fileBucketManager.value?.isDeleting ?? false)
const bucketError = computed(() => fileBucketManager.value?.error ?? null)

const effectiveError = computed(() => errorMessage.value ?? bucketError.value ?? fieldErrorMessage.value)

// Initialiser avec defaultValue
watch(() => props.defaultValue, (newValue) => {
  if (typeof newValue === 'string' && newValue.trim().length > 0 && !didInit.value) {
    uploadedVideo.value = {
      url: newValue,
      name: extractFileNameFromUrl(newValue) ?? newValue,
      bucketFilename: extractFileNameFromUrl(newValue) ?? undefined,
    }
    setValue(newValue, false)
    didInit.value = true
  }
}, { immediate: true })

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleDrag = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = e.type === 'dragenter' || e.type === 'dragover'
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = false
  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    handleFile(e.dataTransfer.files[0])
  }
}

const handleFile = async (file: File) => {
  if (!file.type.startsWith('video/')) {
    alert('Veuillez sélectionner un fichier vidéo valide')
    return
  }

  if (!hasUploadSupport.value) {
    errorMessage.value = 'Configuration d\'upload manquante pour ce champ (fileToBucketManage).'
    return
  }

  fileBucketManager.value?.resetError()
  errorMessage.value = null

  const localUrl = URL.createObjectURL(file)
  localPreviewUrl.value = localUrl

  try {
    const result = await fileBucketManager.value!.uploadFile({ file })
    uploadedVideo.value = {
      url: result.url,
      name: result.originalName ?? result.filename ?? extractFileNameFromUrl(result.url) ?? file.name,
      size: file.size,
      type: file.type,
      bucketFilename: result.filename ?? extractFileNameFromUrl(result.url),
    }
    setValue(result.url, true)
    setTouched(true)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Échec de l\'upload de la vidéo.'
    errorMessage.value = message
  } finally {
    URL.revokeObjectURL(localUrl)
    localPreviewUrl.value = null
  }
}

const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFile(target.files[0])
  }
}

const removeVideo = async () => {
  fileBucketManager.value?.resetError()
  errorMessage.value = null

  if (uploadedVideo.value && hasDeleteSupport.value) {
    try {
      await fileBucketManager.value!.deleteFile({
        filename: uploadedVideo.value.bucketFilename ?? extractFileNameFromUrl(uploadedVideo.value.url ?? '') ?? undefined,
        fileUrl: uploadedVideo.value.url,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Échec de la suppression du fichier distant.'
      errorMessage.value = message
      return
    }
  }

  uploadedVideo.value = null
  localPreviewUrl.value = null
  setValue(null, true)
  setTouched(true)
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}
</script>

<template>
  <div class="video-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="video-field-label-container">
      <span class="video-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div class="space-y-4">
        <!-- Zone de drop -->
        <div
          v-if="!uploadedVideo && !localPreviewUrl"
          :class="[
            'relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out',
            dragActive
              ? 'border-[#04406E] bg-[#e6f0f5] scale-[1.02]'
              : 'border-gray-300 hover:border-[#04406E] hover:bg-gray-50',
            props.className,
            isUploading && 'pointer-events-none opacity-60'
          ]"
          @dragenter="handleDrag"
          @dragleave="handleDrag"
          @dragover="handleDrag"
          @drop="handleDrop"
          @click="fileInputRef?.click()"
        >
          <input
            ref="fileInputRef"
            type="file"
            accept="video/*"
            class="hidden"
            @change="handleInputChange"
          />

          <div class="p-8 text-center">
            <div class="flex flex-col items-center space-y-4">
              <div
                :class="[
                  'p-3 rounded-full transition',
                  dragActive
                    ? 'bg-[#04406E] text-white'
                    : 'bg-gray-100 text-[#04406E] hover:bg-[#e6f0f5]'
                ]"
              >
                <Video :size="24" />
              </div>

              <div>
                <p class="text-base font-medium text-gray-700 mb-1">
                  {{ dragActive ? 'Déposez votre vidéo ici' : 'Glissez-déposez votre vidéo' }}
                </p>
                <p class="text-sm text-gray-500">
                  ou
                  <span class="text-[#04406E] font-medium hover:underline">
                    cliquez pour parcourir
                  </span>
                </p>
                <p class="text-xs text-gray-400 mt-2">
                  Formats acceptés : MP4, WEBM, AVI, etc.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview de la vidéo -->
        <div
          v-if="uploadedVideo || localPreviewUrl"
          class="space-y-3"
        >
          <div class="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
            <div class="flex items-center space-x-3">
              <div class="p-2 bg-purple-50 rounded-lg">
                <Video :size="20" class="text-purple-600" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-gray-800 truncate">
                  {{ uploadedVideo?.name }}
                </p>
                <p v-if="uploadedVideo?.size" class="text-sm text-gray-500">
                  {{ formatFileSize(uploadedVideo.size) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              @click="removeVideo"
              :disabled="isDeleting"
              class="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-60"
            >
              <Loader2 v-if="isDeleting" :size="20" class="animate-spin text-red-500" />
              <Trash2 v-else :size="20" class="text-red-500" />
            </button>
          </div>

          <div class="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            <video
              :src="localPreviewUrl ?? uploadedVideo?.url ?? undefined"
              controls
              class="w-full h-auto"
              style="max-height: 400px"
            />
          </div>
        </div>

        <div
          v-if="effectiveError"
          class="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600"
        >
          {{ effectiveError }}
        </div>
      </div>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.video-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.video-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.video-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

